import { useState, useMemo } from "react"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { EventList } from "./EventList"
import { EventsActionButtons } from "@components/MemberNew/EventsActionButtons"
import { EVENT_TYPE_OPTIONS, useEventsSerializer } from "./utils"

type TEventsTimelineProps = {
  prospect_guid: string | undefined
  account_id?: string
  sectionTitle?: string
  showActionButtons?: boolean
  eventType: "account" | "prospect"
}

export const EventsTimeline = ({
  prospect_guid,
  account_id,
  sectionTitle = "Account Events",
  showActionButtons = true,
  eventType,
}: TEventsTimelineProps) => {
  const getEvents = useEventsSerializer(prospect_guid, account_id, eventType)

  const [selected, setSelected] =
    useState<(typeof EVENT_TYPE_OPTIONS)[number]["value"]>("mx")

  const filtered = useMemo(() => {
    if (!getEvents.data) return []
    const timeStamp = (d?: string | null) => (d ? Date.parse(d) : 0)

    const filteredEvents = getEvents.data?.filter((evt) => {
      switch (selected) {
        case "all":
          return !!evt.type
        case "auto":
          return ["Mail", "Email", "Referral"].includes(evt.type)
        case "cases":
          return evt.type === "Case"
        case "mx":
          return [
            "Note",
            "Contact",
            "SMS",
            "NPS",
            "CSAT",
            "Scheduling",
            "Gladly",
          ].includes(evt.type)
        case "plan":
          return ["Order", "Account"].includes(evt.type)
        case "web":
          return evt.type === "Web"
        default:
          return true
      }
    })

    return filteredEvents.sort((a, b) => timeStamp(b.date) - timeStamp(a.date))
  }, [getEvents.data, selected])

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <div className="tw:flex tw:items-center tw:justify-between">
        <h4 className="tw:text-xl tw:font-semibold">{sectionTitle}</h4>
        {showActionButtons && (
          <EventsActionButtons prospect_guid={prospect_guid} />
        )}
      </div>

      <Select
        value={selected}
        onValueChange={(val) => setSelected(val as typeof selected)}
      >
        <SelectTrigger className="tw:w-52">
          <SelectValue placeholder="Select Event Type" />
        </SelectTrigger>
        <SelectContent>
          {EVENT_TYPE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="tw:max-h-[450px] tw:overflow-y-auto">
        {getEvents.isPending ? (
          <div className="tw:p-8 tw:text-center">Loading Events…</div>
        ) : getEvents.isError ? (
          <div className="tw:p-8 tw:text-red-500">
            Error loading events: {getEvents.error.message}
          </div>
        ) : (
          <EventList events={filtered} />
        )}
      </div>
    </div>
  )
}
